import { ReactNode } from 'react';
import { ButtonIcon, FieldLabel, Tooltip, TooltipTrigger } from '@backstage/ui';
import { RiInformationLine } from '@remixicon/react';

import styles from './Field.module.css';

export type FieldHeaderProps = {
  /** Visible label. Without it the header is not rendered. */
  label?: string;
  /** Id of the control the label is for (native inputs). */
  htmlFor?: string;
  /** Id of the label element, for `aria-labelledby` of React Aria controls. */
  labelId?: string;
  /** Text of the info button next to the label. */
  tooltip?: ReactNode;
  /** Shows a red asterisk after the label. Use `required` for the semantics. */
  requiredMark?: boolean;
  /** Element at the right end of the label row, for example a link. */
  actionItem?: ReactNode;
};

/**
 * Label row of the form fields: BUI `FieldLabel`, required mark, info tooltip
 * and an optional action on the right.
 */
export const FieldHeader = ({
  label,
  htmlFor,
  labelId,
  tooltip,
  requiredMark,
  actionItem,
}: FieldHeaderProps) => {
  if (!label) {
    return null;
  }

  return (
    <div className={`${styles.header} xlrt-FieldHeader`}>
      <div className={styles.labelGroup}>
        <FieldLabel
          label={label}
          htmlFor={htmlFor}
          id={labelId}
          className={styles.label}
        />
        {requiredMark && (
          <span className={styles.requiredMark} aria-hidden="true">
            *
          </span>
        )}
        {tooltip && (
          <TooltipTrigger delay={300}>
            <ButtonIcon
              variant="tertiary"
              size="small"
              icon={<RiInformationLine aria-hidden="true" />}
              aria-label={`More information about ${label}`}
              className={styles.infoButton}
            />
            <Tooltip className={styles.tooltip}>{tooltip}</Tooltip>
          </TooltipTrigger>
        )}
      </div>
      {actionItem && <div className={styles.action}>{actionItem}</div>}
    </div>
  );
};

export type FieldMessageProps = {
  id?: string;
  /** Renders the message in the error color. */
  invalid?: boolean;
  children?: ReactNode;
};

/** Helper or error text below a form field. Renders nothing when empty. */
export const FieldMessage = ({ id, invalid, children }: FieldMessageProps) => {
  if (children === undefined || children === null || children === '') {
    return null;
  }

  return (
    <div
      id={id}
      className={`${styles.message} xlrt-FieldMessage`}
      data-invalid={invalid || undefined}
    >
      {children}
    </div>
  );
};

/** Joins ids for `aria-describedby`, skipping empty ones. */
export const joinIds = (...ids: (string | false | undefined)[]) =>
  ids.filter(Boolean).join(' ') || undefined;
